import { Component, type ReactNode, type ErrorInfo } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { Link } from 'react-router-dom'
import i18n from '../i18n'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Surface the error to the console in development. In production,
    // a monitoring system would replace this hook.
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, errorInfo)
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const t = i18n.t.bind(i18n)

      return (
        <div className="min-h-[60vh] flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-error-light mb-6">
              <AlertTriangle className="text-error-text" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-text-primary mb-3">
              {t('errors.boundary.title')}
            </h2>
            <p className="text-text-secondary mb-8">
              {t('errors.boundary.message')}
            </p>
            {import.meta.env.DEV && this.state.error && (
              <pre className="mb-6 p-4 bg-bg-secondary border border-border rounded-lg text-xs text-left text-text-secondary overflow-auto max-h-40">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover transition-colors"
              >
                <RefreshCw size={18} />
                {t('errors.boundary.retry')}
              </button>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-bg-card text-text-primary rounded-lg font-medium border border-border hover:border-primary-light transition-all"
              >
                <Home size={18} />
                {t('errors.boundary.home')}
              </Link>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export type { ErrorBoundaryProps }
